import React, { useState, useEffect } from 'react';
import { 
  Search, 
  Settings, 
  User, 
  LogOut, 
  Monitor,
  Sun,
  Moon,
  ChevronDown,
  Building,
  CreditCard,
  Shield,
  HelpCircle,
  Key,
  Users,
  Zap,
  Archive
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { 
  DropdownMenu, 
  DropdownMenuContent, 
  DropdownMenuItem, 
  DropdownMenuSeparator, 
  DropdownMenuTrigger,
  DropdownMenuLabel,
  DropdownMenuGroup,
  DropdownMenuSub,
  DropdownMenuSubTrigger,
  DropdownMenuSubContent
} from '@/components/ui/dropdown-menu';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { useTheme } from '@/contexts/ThemeContext';
import { useWorkspace } from '@/contexts/WorkspaceContext';
import { useAuth } from '@/hooks/useAuth';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import * as selectionApi from '@/services/selection/workspaces';
import { OptimizedLogo } from '@/components/OptimizedLogo';
import { MobileSearchButton } from '@/components/MobileSearchButton';
import { useModules } from '@/contexts/ModulesContext';
import { format, parseISO } from 'date-fns';
import { CalendarDays } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
// Archived for now: notifications & messages (will re-enable later)
// import { NotificationDropdown } from '@/components/notifications/NotificationDropdown';
// import { MessageDropdown } from '@/components/notifications/MessageDropdown';
// Static mode: no create workspace modal or live wallet/trial calls

// Real workspace data integration (static for now to avoid duplicate API calls)
const useWorkspaceData = () => {
  const { currentWorkspace } = useWorkspace();
  return {
    workspaces: [] as Array<{ id: string; name: string; status: string; members: number; role: string }>,
    isLoading: false,
    currentWorkspace,
  };
};

// Real-time search hook
const useGlobalSearch = (query: string, workspaceId?: string) => {
  // Static mode: disable live search; return empty results
  return { searchResults: [] as any[], isLoading: false };
};

export function SmartHeader() {
  const { theme, setTheme } = useTheme();
  const { currentWorkspace } = useWorkspace();
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const { workspaces, isLoading: workspacesLoading } = useWorkspaceData();
  const modulesCtx = useModules();
  // Resolve display name immediately from multiple sources (sessionStorage, auth hook, fallback email)
  const getInitialDisplayName = () => {
    if (typeof window !== 'undefined') {
      try {
        const raw = sessionStorage.getItem('userData');
        if (raw) {
          const parsed = JSON.parse(raw);
          // username (email) should be primary display per requirement; keep nameOfUser separately
          if (parsed?.username) return parsed.username as string;
          if (parsed?.email) return parsed.email as string;
          if (parsed?.nameOfUser) return parsed.nameOfUser as string; // fallback to friendly name
        }
      } catch (_) {}
      // Sometimes login flow may have staged data in localStorage before normalization
      try {
        const rawLocal = localStorage.getItem('userData');
        if (rawLocal) {
          const parsed = JSON.parse(rawLocal);
          if (parsed?.username) return parsed.username as string;
          if (parsed?.email) return parsed.email as string;
          if (parsed?.nameOfUser) return parsed.nameOfUser as string;
        }
      } catch (_) {}
    }
    return user?.email || user?.name || 'User';
  };
  const [displayName, setDisplayName] = useState<string>(getInitialDisplayName());
  // Also derive friendly name (nameOfUser) for potential dashboard usage
  const getFriendlyName = () => {
    if (typeof window !== 'undefined') {
      try {
        const raw = sessionStorage.getItem('userData');
        if (raw) { const p = JSON.parse(raw); if (p?.nameOfUser) return p.nameOfUser as string; }
      } catch(_){}
    }
    return user?.name || null;
  };
  const friendlyName = getFriendlyName();
  const displayInitial = (friendlyName || displayName || 'U').charAt(0).toUpperCase();

  // After login, sessionStorage.userData may be written slightly after the header mounts.
  // Use a short-lived polling loop (max ~1s) to capture late population and update once.
  useEffect(() => {
    let attempts = 0;
    const maxAttempts = 10; // 10 * 100ms = 1s
    const interval = setInterval(() => {
      attempts++;
      const fresh = getInitialDisplayName();
      if (fresh && fresh !== displayName && fresh !== 'User') {
        setDisplayName(fresh);
        clearInterval(interval);
      } else if (attempts >= maxAttempts) {
        clearInterval(interval);
      }
    }, 100);
    return () => clearInterval(interval);
    // Re-run if the auth hook user changes (e.g., post-OTP hydration)
  }, [user]);
  
  const [searchQuery, setSearchQuery] = useState('');
  const [searchOpen, setSearchOpen] = useState(false);
  // Static mode: no create workspace modal
  
  // Credit wallet and trial data
  // Static mode: credit wallet and trial disabled
  const wallet = null as any;
  const balanceStatus = 'healthy' as const;
  const trial = null as any;
  const trialInfo = null as any;
  
  // Real-time search
  const { searchResults, isLoading: searchLoading } = useGlobalSearch(searchQuery, currentWorkspace?.id);
  // Static mode: inbox features disabled
  // const unreadCount = 0;

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setSearchOpen(true);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  const handleLogout = () => {
    signOut();
    navigate('/auth');
  };

  const handleNavigation = (path: string) => {
    // If user is on Trial, gate workspace routes to Manage Subscription
    try {
      const rawPlan = String(modulesCtx?.subscription?.planName || '').trim().toLowerCase();
      const isTrialPlan = rawPlan === 'trial' || rawPlan === 'trail'; // tolerate common typo
      if (isTrialPlan && path.startsWith('/workspace/')) {
        // Route to Manage Subscription instead of workspace pages
        navigate('/dashboard/manage-subscription');
        return;
      }
    } catch {}

    // Handle global vs workspace-aware routes
    if (path.startsWith('/account/')) {
      // Account routes are global
      navigate(path);
    } else if (path.startsWith('/workspace/')) {
      // Workspace routes need workspace context
      const workspacePath = path.replace('/workspace/', '');
      // In static mode, always route under /dashboard
      navigate(`/dashboard/${workspacePath}`);
    } else {
      navigate(path);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'healthy': return 'bg-emerald-500';
      case 'warning': return 'bg-yellow-500';
      case 'error': return 'bg-red-500';
      default: return 'bg-gray-500';
    }
  };

  // Workspace switcher: lazy-load available workspaces when the menu opens
  const [wsMenuOpen, setWsMenuOpen] = useState(false);
  // Compute authUserId synchronously from multiple sources
  const computeAuthUserId = () => {
    // Prefer hook user id
    if (user?.id) return String(user.id);
    // Try sessionStorage userData
    try {
      const raw = sessionStorage.getItem('userData');
      if (raw) {
        const p = JSON.parse(raw);
        const id = p?.authUserId || p?.AuthUserId || p?.userId || p?.id;
        if (id) return String(id);
      }
    } catch {}
    // Try in-memory authStore
    try {
      const store = require('@/services/authStore').getAuthStore?.();
      const id = store?.userData?.authUserId || store?.userData?.id || store?.userData?.userId;
      if (id) return String(id);
    } catch {}
    return null;
  };
  const authUserIdForSwitch = computeAuthUserId();

  const workspacesQuery = useQuery({
    queryKey: ['workspaces-switch', authUserIdForSwitch],
    queryFn: async () => {
      const list = await selectionApi.getUserWorkspaces(authUserIdForSwitch || undefined);
      return Array.isArray(list) ? list : [];
    },
    enabled: wsMenuOpen && !!authUserIdForSwitch,
    refetchOnWindowFocus: false,
    staleTime: 15 * 60 * 1000, // 15 minutes
    retry: 1,
  });

  type SwitchItem = {
    id: string;
    name: string;
    role?: string | null;
    userId?: string | null;
    authUserId?: string | null;
    modules?: any[] | null;
    subscription?: any | null;
    raw: any;
  };

  const switchItems: SwitchItem[] = (workspacesQuery.data || []).map((it: any) => ({
    id: String(it.workspaceId || it.workspace_id || it.id),
    name: it.workspaceName || it.workspace_name || it.name || 'Workspace',
    role: it.role || null,
    userId: it.userId ? String(it.userId) : (it.user_id ? String(it.user_id) : null),
    authUserId: it.authUserId ? String(it.authUserId) : (it.AuthUserId ? String(it.AuthUserId) : null),
    modules: it.modules || null,
    subscription: it.subscription || null,
    raw: it,
  }));

  const handleWorkspaceSwitch = async (item: SwitchItem) => {
    const wsId = String(item.id);
    try {
      // Persist selection to session
      try { sessionStorage.setItem('currentWorkspaceId', wsId); } catch {}
      const workspaceData = {
        workspaceId: wsId,
        workspaceName: item.name,
        userId: item.userId ?? null,
        role: item.role ?? null,
        authUserId: item.authUserId ?? null,
      };
      try { sessionStorage.setItem('workspaceData', JSON.stringify(workspaceData)); } catch {}
      try { require('@/services/authStore').setAuthStore({ workspaceData }); } catch {}

      // Persist modules/subscription if provided by the API
      if (item.modules !== undefined) {
        try { localStorage.setItem(`modules:${wsId}`, JSON.stringify(item.modules || [])); } catch {}
      }
      if (item.subscription !== undefined) {
        try { localStorage.setItem(`subscription:${wsId}`, JSON.stringify(item.subscription || null)); } catch {}
      }
    } catch {}

    // Close menu and navigate to dashboard to re-mount providers cleanly
    setWsMenuOpen(false);
    navigate('/dashboard');
  };

  return (
    <header className="h-16 border-b border-border/40 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 sticky top-0 z-50 header-backdrop">
      <div className="flex items-center justify-between px-4 sm:px-6 h-full">
        {/* Left Side - Logo & Search */}
        <div className="flex items-center space-x-2 sm:space-x-4">
          {/* Logo */}
          <div className="flex items-center space-x-2">
            <OptimizedLogo size="sm" variant="icon" />
            <span className="hidden sm:block font-semibold text-lg">BillPortal</span>
          </div>

          {/* Global Search */}
          <Popover open={searchOpen} onOpenChange={setSearchOpen}>
            <PopoverTrigger asChild>
              <div className="relative w-48 sm:w-64 lg:w-96 hidden xs:block">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  type="search"
                  placeholder="Search everything... (⌘K)"
                  className="pl-9 bg-muted/30 border-muted focus:bg-background"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onFocus={() => setSearchOpen(true)}
                />
              </div>
            </PopoverTrigger>
            <PopoverContent className="w-96 p-0" align="start">
              <div className="p-2 space-y-1">
                <div className="px-2 py-1 text-xs font-medium text-muted-foreground">
                  Search Results {searchResults.length > 0 && `(${searchResults.length})`}
                  {searchLoading && " (Loading...)"}
                </div>
                {searchResults.length > 0 ? (
                  searchResults.map((result) => (
                    <button
                      key={result.id}
                      className="w-full px-2 py-2 text-left hover:bg-muted/50 rounded-md text-sm focus:bg-muted/50 focus:outline-none"
                      onClick={() => {
                        navigate(result.href);
                        setSearchOpen(false);
                        setSearchQuery('');
                      }}
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="font-medium">{result.title}</div>
                          <div className="text-xs text-muted-foreground">
                            {result.category} {result.subtitle && `• ${result.subtitle}`}
                          </div>
                        </div>
                      </div>
                    </button>
                  ))
                ) : (
                  <div className="px-2 py-8 text-center text-sm text-muted-foreground">
                    {searchQuery ? `No results found for "${searchQuery}"` : 'Start typing to search...'}
                  </div>
                )}
              </div>
            </PopoverContent>
          </Popover>
        </div>

        {/* Right Side - Actions & Profile */}
        <div className="flex items-center space-x-3">
          {/* Mobile Search Button */}
          <MobileSearchButton onClick={() => setSearchOpen(true)} />
          
          {/* Workspace Switcher */}
          <DropdownMenu open={wsMenuOpen} onOpenChange={setWsMenuOpen}>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="flex items-center space-x-2 px-3">
                <div className="flex items-center space-x-2">
                  <div className={`w-2 h-2 rounded-full ${getStatusColor('healthy')}`} />
                  <span className="hidden md:block font-medium">
                    {currentWorkspace?.name || 'Default Workspace'}
                  </span>
                  <ChevronDown className="h-4 w-4 text-muted-foreground" />
                </div>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent className="w-64" align="end">
              <DropdownMenuLabel>Switch Workspace</DropdownMenuLabel>
              <DropdownMenuSeparator />
              {workspacesQuery.isLoading ? (
                <div className="p-3 text-center text-sm text-muted-foreground">Loading...</div>
              ) : switchItems.length > 0 ? (
                switchItems.map((ws) => (
                  <DropdownMenuItem
                    key={ws.id}
                    className="flex items-center space-x-2 p-3 cursor-pointer"
                    onClick={() => handleWorkspaceSwitch(ws)}
                  >
                    <div className={`w-2 h-2 rounded-full ${getStatusColor('healthy')}`} />
                    <div className="flex-1">
                      <div className="font-medium">{ws.name}</div>
                      {ws.role && (
                        <div className="text-xs text-muted-foreground">{ws.role}</div>
                      )}
                    </div>
                  </DropdownMenuItem>
                ))
              ) : (
                <div className="p-3 text-center text-sm text-muted-foreground">No workspaces found</div>
              )}
            </DropdownMenuContent>
          </DropdownMenu>

          {/* Subscription badge next to workspace selector with tooltip */}
          {modulesCtx?.subscription?.planName && (
            <Tooltip>
              <TooltipTrigger asChild>
                <Badge variant="secondary" className="text-[11px] flex items-center gap-1.5">
                  {(() => {
                    const sub = modulesCtx.subscription!;
                    const plan = String(sub.planName || '').trim();
                    const planLower = plan.toLowerCase();
                    const isTrial = planLower === 'trial' || planLower === 'trail';
                    if (isTrial) {
                      const credits = sub.credits ?? null;
                      const ends = sub.subscriptionEnds ? (() => {
                        try { return format(parseISO(sub.subscriptionEnds as any), 'dd MMM yyyy'); } catch { return null; }
                      })() : null;
                      return (
                        <>
                          <span className="font-medium">{plan}</span>
                          {typeof credits === 'number' && (
                            <span className="inline-flex items-center gap-1 text-muted-foreground">
                              • <CreditCard className="h-3 w-3" /> {credits}
                            </span>
                          )}
                          {ends && (
                            <span className="inline-flex items-center gap-1 text-muted-foreground">
                              • <CalendarDays className="h-3 w-3" /> {ends}
                            </span>
                          )}
                        </>
                      );
                    }
                    return <span className="font-medium">{plan}</span>;
                  })()}
                </Badge>
              </TooltipTrigger>
              <TooltipContent>
                {(() => {
                  const sub = modulesCtx.subscription!;
                  const plan = String(sub.planName || '').trim();
                  const credits = sub.credits ?? null;
                  const endsRaw = sub.subscriptionEnds || null;
                  let endsFmt: string | null = null;
                  if (endsRaw) { try { endsFmt = format(parseISO(endsRaw as any), 'PPpp'); } catch { endsFmt = null; } }
                  return (
                    <div className="text-xs">
                      <div className="font-semibold">{plan}</div>
                      {typeof credits === 'number' && <div>Credits: {credits}</div>}
                      {endsFmt && <div>Expires: {endsFmt}</div>}
                    </div>
                  );
                })()}
              </TooltipContent>
            </Tooltip>
          )}

          {/* Credit balance and trial indicators disabled in static mode */}

          {/** Inbox components disabled (MessageDropdown / NotificationDropdown) */}

          {/* Profile Menu */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="relative h-8 w-8 rounded-full">
                <Avatar className="h-8 w-8">
                  <AvatarFallback className="bg-primary text-primary-foreground">
                    {displayInitial}
                  </AvatarFallback>
                </Avatar>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent className="w-64" align="end">
              {/* User Info */}
              <div className="p-3 border-b">
                <p className="text-sm font-medium">{displayName}</p>
                <p className="text-xs text-muted-foreground">{modulesCtx?.subscription?.planName || 'Plan'} • User</p>
              </div>

              {/* Account Section */}
              <DropdownMenuGroup>
                <DropdownMenuLabel>Account</DropdownMenuLabel>
                <DropdownMenuItem onClick={() => handleNavigation('/account/profile')}>
                  <User className="mr-2 h-4 w-4" />
                  Profile Settings
                </DropdownMenuItem>
                {/* <DropdownMenuItem onClick={() => handleNavigation('/account/security')}>
                  <Shield className="mr-2 h-4 w-4" />
                  Security & Privacy
                </DropdownMenuItem> */}
                <DropdownMenuItem onClick={() => handleNavigation('/account/password')}>
                  <Key className="mr-2 h-4 w-4" />
                  Change Password
                </DropdownMenuItem>
              </DropdownMenuGroup>

              <DropdownMenuSeparator />

              {/* Workspace Section */}
              <DropdownMenuGroup>
                <DropdownMenuLabel>Workspace</DropdownMenuLabel>
                <DropdownMenuItem onClick={() => handleNavigation('/workspace/settings')}>
                  <Building className="mr-2 h-4 w-4" />
                  Workspace Settings
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => handleNavigation('/workspace/team')}>
                  <Users className="mr-2 h-4 w-4" />
                  Team Management
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => handleNavigation('/workspace/billing')}>
                  <CreditCard className="mr-2 h-4 w-4" />
                  Billing & Usage
                </DropdownMenuItem>
              </DropdownMenuGroup>

              <DropdownMenuSeparator />

              {/* System Section */}
              {/* <DropdownMenuGroup>
                <DropdownMenuLabel>System</DropdownMenuLabel>
                <DropdownMenuItem onClick={() => handleNavigation('/workspace/integrations')}>
                  <Zap className="mr-2 h-4 w-4" />
                  Integrations
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => handleNavigation('/workspace/api')}>
                  <Archive className="mr-2 h-4 w-4" />
                  API Access
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => handleNavigation('/support')}>
                  <HelpCircle className="mr-2 h-4 w-4" />
                  Help & Support
                </DropdownMenuItem>
              </DropdownMenuGroup> */}

              <DropdownMenuSeparator />

              {/* Theme Selector */}
              <DropdownMenuSub>
                <DropdownMenuSubTrigger>
                  <Monitor className="mr-2 h-4 w-4" />
                  Theme
                </DropdownMenuSubTrigger>
                <DropdownMenuSubContent>
                  <DropdownMenuItem onClick={() => setTheme('light')}>
                    <Sun className="mr-2 h-4 w-4" />
                    Light
                  </DropdownMenuItem>
                  {/* <DropdownMenuItem onClick={() => setTheme('dark')}>
                    <Moon className="mr-2 h-4 w-4" />
                    Dark
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => setTheme('system')}>
                    <Monitor className="mr-2 h-4 w-4" />
                    System
                  </DropdownMenuItem> */}
                </DropdownMenuSubContent>
              </DropdownMenuSub>

              <DropdownMenuSeparator />
              
              <DropdownMenuItem onClick={handleLogout}>
                <LogOut className="mr-2 h-4 w-4" />
                Logout
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
      
      {/* Create Workspace Modal removed in static mode */}
    </header>
  );
}
